import React, { useEffect, useState } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { Fragment } from 'react';
import { ChatBubbleLeftRightIcon, ExclamationTriangleIcon, PlusCircleIcon, MinusCircleIcon } from '@heroicons/react/24/outline';
import { workspaceService, WorkspaceForDocument } from '../../../services/workspaceService';
import { extractAccessErrorMessage } from '../../../utils/featureAccessError';
import { FeatureAccessState } from '../../UI/FeatureAccessState';

interface ManageChatbotsModalProps {
  open: boolean;
  documentId: string;
  currentlyIndexedWorkspaceIds: string[];
  onClose: () => void;
  onConfirm: (workspacesToAdd: string[], workspacesToRemove: string[]) => void;
  isSubmitting: boolean;
}

export const ManageChatbotsModal: React.FC<ManageChatbotsModalProps> = ({
  open,
  documentId,
  currentlyIndexedWorkspaceIds,
  onClose,
  onConfirm,
  isSubmitting,
}) => {
  const [workspaces, setWorkspaces] = useState<WorkspaceForDocument[]>([]);
  const [selectedWorkspaceIds, setSelectedWorkspaceIds] = useState<Set<string>>(new Set());
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [isFeatureError, setIsFeatureError] = useState(false);

  useEffect(() => {
    if (!open)
    {
      return;
    }

    let cancelled = false;

    const load = async () => {
      setIsLoading(true);
      setErrorMessage('');
      setIsFeatureError(false);
      
      try {
        const list = await workspaceService.getWorkspacesForDocument(documentId, {
          skipFeatureUnavailableToast: true,
        });
        
        if (!cancelled)
        {
          setWorkspaces(list);
          setSelectedWorkspaceIds(new Set(currentlyIndexedWorkspaceIds));
        }
      } catch (error: unknown) {
        if (!cancelled)
        {
          const accessMessage = extractAccessErrorMessage(error);
          
          if (accessMessage)
          {
            setErrorMessage(accessMessage);
            setIsFeatureError(true);
          }
          else
          {
            setErrorMessage('Não foi possível carregar os agentes. Tente novamente.');
            setIsFeatureError(false);
          }
          
          setWorkspaces([]);
        }
      } finally {
        if (!cancelled)
        {
          setIsLoading(false);
        }
      }
    };

    void load();
    return () => {
      cancelled = true;
    };
  }, [documentId, open, currentlyIndexedWorkspaceIds]);

  const hasError = !!errorMessage;
  const noAgents = !isLoading && workspaces.length === 0 && !hasError;
  
  const initialIndexedSet = new Set(currentlyIndexedWorkspaceIds);
  const workspacesToAdd = Array.from(selectedWorkspaceIds).filter(id => !initialIndexedSet.has(id));
  const workspacesToRemove = currentlyIndexedWorkspaceIds.filter(id => !selectedWorkspaceIds.has(id));
  const hasChanges = workspacesToAdd.length > 0 || workspacesToRemove.length > 0;
  const canConfirm = hasChanges && !isSubmitting;
  
  const hasOnlyRemovals = workspacesToRemove.length > 0 && workspacesToAdd.length === 0;
  const buttonColorClass = hasOnlyRemovals
    ? 'bg-red-600 hover:bg-red-700'
    : 'bg-blue-600 hover:bg-blue-700';

  const toggleWorkspace = (workspaceId: string) => {
    setSelectedWorkspaceIds(prev => {
      const next = new Set(prev);
      
      if (next.has(workspaceId))
      {
        next.delete(workspaceId);
      }
      else
      {
        next.add(workspaceId);
      }
      
      return next;
    });
  };

  const handleConfirm = () => {
    onConfirm(workspacesToAdd, workspacesToRemove);
  };

  return (
    <Transition appear show={open} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={() => !isSubmitting && onClose()}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-200"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-150"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/40" aria-hidden="true" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-200"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-150"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-md rounded-lg bg-white shadow-xl">
                <div className="flex items-center justify-between px-6 py-4 border-b bg-gray-50 rounded-t-lg">
                  <div className="flex items-center gap-2">
                    <ChatBubbleLeftRightIcon className="h-5 w-5 text-blue-600" />
                    <Dialog.Title className="text-base font-semibold text-gray-900">
                      Gerenciar Chatbots
                    </Dialog.Title>
                  </div>
                  <button
                    onClick={onClose}
                    disabled={isSubmitting}
                    className="text-gray-400 hover:text-gray-600 text-2xl leading-none p-1 rounded hover:bg-gray-100 disabled:opacity-50"
                    aria-label="Fechar"
                  >
                    ×
                  </button>
                </div>

                <div className="px-6 py-5">
                  {isLoading && (
                    <div className="flex items-center justify-center py-10">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
                    </div>
                  )}

                  {hasError && (
                    <FeatureAccessState
                      variant={isFeatureError ? 'feature' : 'error'}
                      message={errorMessage}
                      compact
                    />
                  )}

                  {noAgents && (
                    <div className="flex flex-col items-center gap-3 py-6 text-center">
                      <div className="flex items-center justify-center h-12 w-12 rounded-full bg-amber-100">
                        <ExclamationTriangleIcon className="h-6 w-6 text-amber-600" />
                      </div>
                      <div>
                        <p className="font-medium text-gray-800">Nenhum agente disponível</p>
                        <p className="mt-1 text-sm text-gray-500">
                          Não há agentes configurados para este documento.
                        </p>
                      </div>
                    </div>
                  )}

                  {!isLoading && workspaces.length > 0 && (
                    <div className="relative">
                      {isSubmitting && (
                        <div className="absolute inset-0 bg-white/60 backdrop-blur-[1px] flex items-center justify-center z-10 rounded-lg">
                          <div className="flex flex-col items-center gap-2">
                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
                            <p className="text-sm font-medium text-gray-700">Processando alterações...</p>
                          </div>
                        </div>
                      )}
                      <p className="text-sm text-gray-600 mb-4">
                        Selecione os agentes nos quais deseja indexar este documento:
                      </p>
                      <div className="space-y-2 max-h-96 overflow-y-auto">
                        {workspaces.map(workspace => {
                          const isSelected = selectedWorkspaceIds.has(workspace.id);
                          const isRestricted = !workspace.isAccessible;
                          const wasInitiallyIndexed = currentlyIndexedWorkspaceIds.includes(workspace.id);

                          return (
                            <label
                              key={workspace.id}
                              className={`flex items-start gap-3 p-3 rounded-lg border transition-colors ${
                                isRestricted || isSubmitting
                                  ? 'border-gray-200 bg-gray-50 opacity-60 cursor-not-allowed'
                                  : isSelected
                                  ? 'border-blue-400 bg-blue-50 cursor-pointer'
                                  : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50 cursor-pointer'
                              }`}
                            >
                              <input
                                type="checkbox"
                                value={workspace.id}
                                checked={isSelected}
                                disabled={isRestricted || isSubmitting}
                                onChange={() => !isRestricted && !isSubmitting && toggleWorkspace(workspace.id)}
                                className="mt-0.5 h-4 w-4 rounded text-blue-600 border-gray-300 focus:ring-blue-500 disabled:opacity-50"
                              />
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 flex-wrap">
                                  <p className="text-sm font-medium text-gray-900">
                                    {workspace.name}
                                  </p>
                                  {wasInitiallyIndexed && (
                                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium bg-green-100 text-green-800">
                                      Já indexado
                                    </span>
                                  )}
                                  {isRestricted && (
                                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium bg-gray-100 text-gray-600">
                                      Restrito
                                    </span>
                                  )}
                                </div>
                                {workspace.description && (
                                  <p className="text-xs text-gray-500 mt-0.5 truncate">
                                    {workspace.description}
                                  </p>
                                )}
                              </div>
                            </label>
                          );
                        })}
                      </div>
                      
                      {hasChanges && (
                        <div className="mt-4 p-3 rounded-lg bg-gray-50 border border-gray-200">
                          <p className="text-xs font-semibold text-gray-700 mb-2">Alterações a aplicar:</p>
                          <div className="space-y-1.5">
                            {workspacesToAdd.length > 0 && (
                              <div className="flex items-center gap-1.5 text-xs text-green-700 bg-green-50 px-2 py-1.5 rounded border border-green-200">
                                <PlusCircleIcon className="h-4 w-4 flex-shrink-0" />
                                <span className="font-medium">
                                  {workspacesToAdd.length} agente{workspacesToAdd.length > 1 ? 's' : ''} para indexar
                                </span>
                              </div>
                            )}
                            {workspacesToRemove.length > 0 && (
                              <div className="flex items-center gap-1.5 text-xs text-red-700 bg-red-50 px-2 py-1.5 rounded border border-red-200">
                                <MinusCircleIcon className="h-4 w-4 flex-shrink-0" />
                                <span className="font-medium">
                                  {workspacesToRemove.length} agente{workspacesToRemove.length > 1 ? 's' : ''} para remover
                                </span>
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>

                <div className="flex justify-end gap-2 px-6 py-4 border-t bg-gray-50 rounded-b-lg">
                  {(noAgents || hasError) ? (
                    <button
                      onClick={onClose}
                      className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
                    >
                      Fechar
                    </button>
                  ) : (
                    <>
                      <button
                        onClick={onClose}
                        disabled={isSubmitting}
                        className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 transition-colors"
                      >
                        Cancelar
                      </button>
                      <button
                        onClick={handleConfirm}
                        disabled={!canConfirm}
                        className={`inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-white rounded-md disabled:opacity-50 disabled:cursor-not-allowed transition-colors ${buttonColorClass}`}
                      >
                        {isSubmitting && (
                          <div className="animate-spin rounded-full h-3.5 w-3.5 border-b-2 border-white" />
                        )}
                        Aplicar
                      </button>
                    </>
                  )}
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};
